import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import { CheckCircle, Mail, Clock, Award } from 'lucide-react';
import { motion } from 'framer-motion';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import { submitContactForm } from '../../api/contact';
import { useSEO } from '../../hooks/useSEO';
import { useScrollTracking } from '../../hooks/useScrollTracking';
import { useClickTracking } from '../../hooks/useClickTracking';
import { ProfessionalServiceSchema, BreadcrumbSchema } from '../../components/JsonLd';
import { trackFormSubmission } from '../../components/Analytics';

// Form validation schema
const formSchema = z?.object({
  serviceInterest: z?.string()?.optional(),
  name: z?.string()?.min(2, "Please enter your name"),
  email: z?.string()?.email("Please enter a valid email address"),
  organization: z?.string()?.optional(),
  message: z?.string()?.min(10, "Please tell us a bit more about how we can help"),
});

const ContactPage = () => {
  useSEO({
    title: 'Contact',
    description: 'Connect with The Poetic Method to discuss strategic partnerships for your healthcare organization. Begin with a consultation to explore institutional alignment.',
  });

  useScrollTracking('contact');
  useClickTracking('contact');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      serviceInterest: '',
      name: '',
      email: '',
      organization: '',
      message: '',
    },
  });

  const serviceInterestValue = watch('serviceInterest');

  const serviceOptions = [
    { value: 'culture-catalyst', label: 'Culture & Team Development' },
    { value: 'leadership-accelerator', label: 'Leadership Development' },
    { value: 'operational-excellence', label: 'Operational Excellence' },
    { value: 'culture-assessment', label: 'Culture Assessment' },
    { value: 'transformation-partnership', label: 'Transformation Partnership' },
    { value: 'speaking', label: 'Speaking Engagement' },
    { value: 'other', label: 'Something else' },
    { value: 'exploring', label: 'Just exploring' },
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitContactForm(data);
      
      if (result?.success) {
        setIsSuccess(true);
        reset();
        // Track form submission for analytics
        trackFormSubmission('contact_form');
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setSubmitError('Something went wrong. Please try again or email us directly at poeticmethod101@gmail.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  const breadcrumbItems = [
    { name: 'Home', url: 'https://thepoeticmethod.com/' },
    { name: 'Contact', url: 'https://thepoeticmethod.com/contact' },
  ];

  return (
    <>
      <ProfessionalServiceSchema />
      <BreadcrumbSchema items={breadcrumbItems} />
      <div className="min-h-screen bg-cream">
        <Header />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 px-4 sm:px-6">
        {/* Hero Section */}
        <motion.div
          className="max-w-4xl mx-auto mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          aria-labelledby="contact-hero-heading"
        >
          <motion.h1
            id="contact-hero-heading"
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-navy mb-5 sm:mb-6"
            variants={itemVariants}
          >
            Let's Start a Conversation
          </motion.h1>
          <motion.p
            className="text-base sm:text-lg md:text-xl text-slate max-w-3xl mb-5 sm:mb-6"
            variants={itemVariants}
          >
            Every transformation begins with a single conversation. Whether you're exploring options or ready to begin, we'd love to hear from you.
          </motion.p>
          
          {/* Expectation Setting */}
          <motion.div 
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 text-xs sm:text-sm text-charcoal"
            variants={itemVariants}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-teal flex-shrink-0" aria-hidden="true" />
              <span>Response within 1-2 business days</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-4 h-4 text-teal flex-shrink-0" aria-hidden="true" />
              <span>Recognized by national media for healthcare innovation</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Two-Column Layout */}
        <motion.div
          className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          {/* Left Column - Contact Form */}
          <motion.div className="lg:col-span-7" variants={itemVariants}>
            {!isSuccess ? (
              <div className="bg-white rounded-xl border border-mist p-6 sm:p-8 shadow-sm">
                {/* Booking Psychology Header */}
                <div className="mb-5 sm:mb-6 pb-5 sm:pb-6 border-b border-mist">
                  <h2 className="font-display text-xl sm:text-2xl text-navy mb-2">Book a Confidential Conversation</h2>
                  <p className="text-xs sm:text-sm text-slate">
                    Typical clients see measurable improvements within 3-6 months of engagement
                  </p>
                </div>
                
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 sm:space-y-6" aria-label="Contact form">
                  {/* Service Interest */}
                  <div>
                    <label
                      htmlFor="serviceInterest"
                      className="block text-xs sm:text-sm font-medium text-navy mb-2"
                    >
                      What brings you here?
                    </label>
                    <Select
                      id="serviceInterest"
                      options={serviceOptions}
                      value={serviceInterestValue}
                      onChange={(value) => setValue('serviceInterest', value)}
                      placeholder="Select an area of interest"
                      className="w-full"
                      aria-label="Select your area of interest"
                    />
                  </div>

                  {/* Name */}
                  <div>
                    <Input
                      id="name"
                      label="Your name"
                      placeholder="Full name"
                      required
                      error={errors?.name?.message}
                      {...register('name')}
                      className="border-navy/20 focus:border-teal focus:ring-teal"
                      aria-required="true"
                      aria-invalid={errors?.name ? 'true' : 'false'}
                      aria-describedby={errors?.name ? 'name-error' : undefined}
                    />
                    {errors?.name && (
                      <p id="name-error" className="text-xs text-red-600 mt-1" role="alert">
                        {errors?.name?.message}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <Input
                      id="email"
                      type="email"
                      label="Email address"
                      placeholder="you@organization.com"
                      required
                      error={errors?.email?.message}
                      {...register('email')}
                      className="border-navy/20 focus:border-teal focus:ring-teal"
                      aria-required="true"
                      aria-invalid={errors?.email ? 'true' : 'false'}
                      aria-describedby={errors?.email ? 'email-error' : undefined}
                    />
                    {errors?.email && (
                      <p id="email-error" className="text-xs text-red-600 mt-1" role="alert">
                        {errors?.email?.message}
                      </p>
                    )}
                  </div>

                  {/* Organization */}
                  <div>
                    <Input
                      id="organization"
                      label="Organization"
                      placeholder="Company or organization name"
                      error={errors?.organization?.message}
                      {...register('organization')}
                      className="border-navy/20 focus:border-teal focus:ring-teal"
                      aria-describedby={errors?.organization ? 'organization-error' : undefined}
                    />
                    {errors?.organization && (
                      <p id="organization-error" className="text-xs text-red-600 mt-1" role="alert">
                        {errors?.organization?.message}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-navy mb-2"
                    >
                      How can we help? <span className="text-coral">*</span>
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      placeholder="Tell us a bit about what you're looking for, or any questions you have..."
                      {...register('message')}
                      className={`w-full rounded-md border px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 ${
                        errors?.message
                          ? 'border-coral focus-visible:ring-coral' :'border-navy/20 focus:border-teal'
                      }`}
                      aria-label="How can we help?"
                    />
                    {errors?.message && (
                      <p className="text-sm text-coral mt-1">{errors?.message?.message}</p>
                    )}
                  </div>

                  {/* Submit Error */}
                  {submitError && (
                    <motion.div 
                      className="p-4 bg-coral/10 border border-coral/20 rounded-lg"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <p className="text-sm text-coral">{submitError}</p>
                    </motion.div>
                  )}

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    className="w-full md:w-auto bg-coral text-white hover:bg-coral/90 px-8 py-3.5 text-base font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:scale-105"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                  
                  <p className="text-xs text-slate mt-4 leading-relaxed">
                    By submitting this form, you agree to receive communications from The Poetic Method™. We respect your privacy and will never share your information.
                  </p>
                </form>
              </div>
            ) : (
              <motion.div
                className="bg-white rounded-xl border border-mist p-12 shadow-sm text-center"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-sage" />
                </div>
                <h3 className="font-display text-2xl text-navy mb-3">Message Received</h3>
                <p className="text-slate max-w-md mx-auto mb-6">
                  Thank you for reaching out. We typically respond within 1-2 business days.
                  We're looking forward to connecting with you.
                </p>
                <Button
                  onClick={() => setIsSuccess(false)}
                  variant="outline"
                  className="border-teal text-teal hover:bg-teal hover:text-white"
                >
                  Send Another Message
                </Button>
              </motion.div>
            )}
          </motion.div>

          {/* Right Column - Additional Info */}
          <motion.div className="lg:col-span-5 space-y-8" variants={itemVariants}>
            {/* Response Time */}
            <div>
              <h3 className="font-display text-lg text-navy mb-2">Response Time</h3>
              <p className="text-slate">
                We typically respond within 1-2 business days. For urgent inquiries, please note
                that in your message.
              </p>
            </div>

            {/* What to Expect */}
            <div>
              <h3 className="font-display text-lg text-navy mb-2">What to Expect</h3>
              <p className="text-slate">
                After receiving your message, we'll schedule a complimentary discovery call to
                understand your needs and explore how The Poetic Method™ might help.
              </p>
            </div>

            {/* Alternative Contact */}
            <div>
              <h3 className="font-display text-lg text-navy mb-2">Prefer Email Directly?</h3>
              <a
                href="mailto:poeticmethod101@gmail.com"
                className="text-teal hover:underline flex items-center gap-2 transition-all duration-200"
              >
                <Mail size={18} />
                poeticmethod101@gmail.com
              </a>
            </div>

            {/* Location Context */}
            <div>
              <h3 className="font-display text-lg text-navy mb-2">Based In</h3>
              <p className="text-slate">South Florida · Serving organizations nationwide</p>
            </div>
          </motion.div>
        </motion.div>

        {/* Trust Element */}
        <motion.section
          className="mt-16 pt-12 border-t border-mist text-center max-w-4xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <p className="text-stone text-sm max-w-2xl mx-auto">
            The Poetic Method™ has partnered with healthcare organizations, leadership teams, and
            executives committed to meaningful transformation. Your inquiry is the first step.
          </p>
        </motion.section>
      </main>
        <Footer />
      </div>
    </>
  );
};

export default ContactPage;